import { getMenuInfo, getCarouselSlides, getBasicInfo } from "../Firebase/Firebase";
import { useState, useEffect, useMemo } from "react";

// Custom hook to fetch menu data from Firebase
export const useMenuData = () => {
    const [menuItems, setMenuItems] = useState([]);
    const [menuCategories, setMenuCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        try {
            getMenuInfo((firebaseData) => {
                try {
                    // Transform Firebase data to match expected format
                    const transformedMenuItems = firebaseData.map(item => {
                        const imageUrls = item.imageUrls || (item.imageUrl ? [item.imageUrl] : []);
                        return {
                            id: item.id || `item-${Date.now()}-${Math.random()}`,
                            name: item.name || 'Untitled',
                            category: item.category || 'Uncategorized',
                            dietaryPreference: item.dietaryPreference || 'veg',
                            imageUrls,
                            imageUrl: item.imageUrl || imageUrls[0] || '',
                            singlePrice: item.singlePrice || '0',
                            hasWeightOptions: item.hasWeightOptions || false,
                            weightOptions: item.weightOptions || [],
                            price: item.price || '0'
                        };
                    });

                    // Extract unique categories from menu items
                    const categories = [
                        { id: 'all', name: 'All' },
                        ...Array.from(new Set(transformedMenuItems.map(item => item.category)))
                            .filter(category => category && category !== 'Uncategorized')
                            .map(category => ({ id: category, name: category }))
                    ];

                    setMenuItems(transformedMenuItems);
                    setMenuCategories(categories);
                    setError(null);
                } catch (transformError) {
                    console.error('Error transforming menu data:', transformError);
                    setError('Failed to process menu data');
                    setMenuItems([]);
                    setMenuCategories([{ id: 'all', name: 'All' }]);
                }
                setLoading(false);
            });
        } catch (error) {
            console.error('Error loading menu data:', error);
            setError('Failed to load menu data');
            setMenuItems([]);
            setMenuCategories([{ id: 'all', name: 'All' }]);
            setLoading(false);
        }
    }, []);

    const sortedMenuItems = useMemo(() => {
        return [...menuItems].sort((a, b) => {
            const nameA = (a.name || '').toLowerCase();
            const nameB = (b.name || '').toLowerCase();
            return nameA.localeCompare(nameB);
        });
    }, [menuItems]);

    return { menuItems: sortedMenuItems, menuCategories, loading, error };
};

// Custom hook to fetch carousel data from Firebase
export const useCarouselData = () => {
    const [carouselSlides, setCarouselSlides] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        try {
            getCarouselSlides((firebaseData) => {
                try {
                    // Transform Firebase data to match expected format
                    const transformedCarouselSlides = firebaseData.map(item => ({
                        id: item.id || `slide-${Date.now()}-${Math.random()}`,
                        imageUrl: item.imageUrl || '',
                        title: item.title || 'Untitled'
                    }));

                    setCarouselSlides(transformedCarouselSlides);
                    setError(null);
                } catch (transformError) {
                    console.error('Error transforming carousel data:', transformError);
                    setError('Failed to process carousel data');
                    setCarouselSlides([]);
                }
                setLoading(false);
            });
        } catch (error) {
            console.error('Error loading carousel data:', error);
            setError('Failed to load carousel data');
            setCarouselSlides([]);
            setLoading(false);
        }
    }, []);

    return { carouselSlides, loading, error };
};

// Custom hook to fetch basic info from Firebase
export const useBasicInfo = () => {
    const [basicInfo, setBasicInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        try {
            getBasicInfo((firebaseData) => {
                try {
                    // Ensure we have valid data with fallbacks
                    const processedData = firebaseData ? {
                        address: firebaseData.address || 'Address not set',
                        email: firebaseData.email || 'email@example.com',
                        contactNo: firebaseData.contactNo || '+91 0000000000',
                        chefImageUrl: firebaseData.chefImageUrl || ''
                    } : {
                        address: 'Address not set',
                        email: 'email@example.com',
                        contactNo: '+91 0000000000',
                        chefImageUrl: ''
                    };

                    setBasicInfo(processedData);
                    setError(null);
                } catch (transformError) {
                    console.error('Error processing basic info:', transformError);
                    setError('Failed to process basic info');
                    setBasicInfo({
                        address: 'Address not set',
                        email: 'email@example.com',
                        contactNo: '+91 0000000000',
                        chefImageUrl: ''
                    });
                }
                setLoading(false);
            });
        } catch (error) {
            console.error('Error loading basic info:', error);
            setError('Failed to load basic info');
            setBasicInfo({
                address: 'Address not set',
                email: 'email@example.com',
                contactNo: '+91 0000000000',
                chefImageUrl: ''
            });
            setLoading(false);
        }
    }, []);

    return { basicInfo, loading, error };
};

// Default categories (fallback)
// export const defaultMenuCategories = [
//     { id: 'all', name: 'All' },
//     { id: 'breads', name: 'Breads' },
//     { id: 'Cheese Cakes', name: 'Cheese Cakes' },
//     { id: 'beverages', name: 'Beverages' }
// ];

// Default menu items (fallback)
// export const defaultMenuItems = [
//     {
//         id: 'biscoff-cheesecake',
//         name: 'Biscoff Cheesecake',
//         category: 'Cheese Cakes',
//         image: 'https://images.pexels.com/photos/28895988/pexels-photo-28895988.jpeg',
//         hasWeightOptions: true,
//         weightOptions: [
//             { weight: '250g', price: 12 },
//             { weight: '500g', price: 20 },
//             { weight: '1kg', price: 35 }
//         ],
//         defaultPrice: 12
//     },
//     {
//         id: 'nutella-cake',
//         name: 'Nutella Cake',
//         category: 'Cheese Cakes',
//         image: 'https://images.pexels.com/photos/11076350/pexels-photo-11076350.jpeg',
//         hasWeightOptions: true,
//         weightOptions: [
//             { weight: '250g', price: 15 },
//             { weight: '500g', price: 25 },
//             { weight: '1kg', price: 45 }
//         ],
//         defaultPrice: 15
//     },
//     {
//         id: 'blueberry-cheesecake',
//         name: 'Blueberry Cheesecake',
//         category: 'Cheese Cakes',
//         image: 'https://images.pexels.com/photos/221068/pexels-photo-221068.jpeg',
//         hasWeightOptions: false,
//         price: 18
//     },
//     {
//         id: 'bento-basque-cheesecake',
//         name: 'Bento Basque Cheesecake',
//         category: 'Cheese Cakes',
//         image: 'https://images.pexels.com/photos/6607325/pexels-photo-6607325.jpeg',
//         hasWeightOptions: true,
//         weightOptions: [
//             { weight: '250g', price: 10 },
//             { weight: '500g', price: 18 }
//         ],
//         defaultPrice: 10
//     },
//     {
//         id: 'pistachio-cheesecake',
//         name: 'Pistachio Cheesecake',
//         category: 'Cheese Cakes',
//         image: 'https://images.pexels.com/photos/29300017/pexels-photo-29300017.jpeg',
//         hasWeightOptions: false,
//         price: 22
//     },
//     {
//         id: 'chocolate-truffle-cake',
//         name: 'Chocolate Truffle Cake',
//         category: 'Cheese Cakes',
//         image: 'https://images.pexels.com/photos/28895988/pexels-photo-28895988.jpeg',
//         hasWeightOptions: true,
//         weightOptions: [
//             { weight: '250g', price: 16 },
//             { weight: '500g', price: 28 },
//             { weight: '1kg', price: 50 }
//         ],
//         defaultPrice: 16
//     }
// ];

// Default carousel slides (fallback)
// export const carouselSlides = [
//     {
//         id: 1,
//         imageUrl: 'https://images.pexels.com/photos/28895988/pexels-photo-28895988.jpeg',
//         title: 'Biscoff Cheesecake',
//     },
//     {
//         id: 2,
//         imageUrl: 'https://images.pexels.com/photos/11076351/pexels-photo-11076351.jpeg',
//         title: 'Nutella Cake',
//     },
//     {
//         id: 3,
//         imageUrl: 'https://images.pexels.com/photos/221068/pexels-photo-221068.jpeg',
//         title: 'Blueberry Cheesecake',
//     },
//     {
//         id: 4,
//         imageUrl: 'https://images.pexels.com/photos/6607325/pexels-photo-6607325.jpeg',
//         title: 'Bento Basque Cheesecake',
//     },
//     {
//         id: 5,
//         imageUrl: 'https://images.pexels.com/photos/29300017/pexels-photo-29300017.jpeg',
//         title: 'Pistachio Cheesecake',
//     }
// ];

